import Resumable from 'resumablejs';
import moment from 'moment';
import { getAuthData } from 'utils/authData';
import { API_URL } from '../constants';
import { getSVRChunkAsset } from '../endpoints/svr';
// regional date handling
import { formatDateForUserShort } from 'utils/regionalDateHelper';

export const ADDITIONAL_CATEGORY_KEY = 'miscellaneous';

export const isSomeReportQuestionFlagged = (svrCategories) => {
  return svrCategories?.some(({ svr_questions }) => svr_questions.some(({ answer }) => answer?.is_flagged));
};

export const getMiscellaneousCategory = (categories) => categories.find((category) => category.is_static);

export function buildCustomSVRCategory(SVRCustomQuestions) {
  return {
    item_key: 'custom',
    name: 'Custom Questions',
    name_key: 'general.customQuestions',
    svr_questions: SVRCustomQuestions.map((question) => ({
      ...question,
      item_key: question.label,
      question_type: 'custom'
    })),
  };
}

export const getAnsweredSVRQuestions = (categories) => {
  return categories.map(({ svr_questions }) => svr_questions
    .filter(({ answer, question_type }) => (question_type === 'common' && answer?.value) ||
    question_type === 'additional_section')
    .map(({ id, answer }) => ({
      id,
      answer: {
        ...answer,
        is_flagged: !!answer?.is_flagged,
        value: answer?.value || null,
      }
    })))
    .flat(1);
};

export const getSVRNotesComment = (answer, user) => ({
  comment: answer.value,
  user,
  created_at: answer.created_at,
  updated_at: answer.updated_at,
});

export const mergeCategoriesAnswers = (oldCategories, newCategories) => {
  return newCategories.map((category) => ({
    ...category,
    svr_questions: category.svr_questions.map((question) => ({
      ...question,
      answer: {
        ...(oldCategories
          .find(({ item_key }) => item_key === category.item_key)?.svr_questions
        ?.find(({ item_key }) => item_key === question.item_key)?.answer || {}),
        id: question.answer.id,
      }
    }))
  }));
};

export const mergeCategoryAnswers = (oldCategory, newCategory) => {
  return {
    ...newCategory,
    svr_questions: newCategory.svr_questions.map((question) => ({
      ...question,
      answer: {
        ...(oldCategory.svr_questions.find(({ item_key }) => item_key === question.item_key)?.answer || {}),
        id: question.answer.id,
      }
    }))
  };
};

export const uploadAssetToSVR = (valueId, asset) => {
  return new Promise((resolve, reject) => {
    const uploadPath = `${API_URL}/svr_question_values/${valueId}/chunk`;
    const R = new Resumable({
      target: uploadPath,
      testTarget: uploadPath,
      headers: { 'Accept': '*/*', ...getAuthData() },
      simultaneousUploads: 1,
      testChunks: false,
      chunkRetryInterval: 500,
      maxFileSize: 314572800,
      method: 'post'
    });

    R.addFile(asset);

    R.on('fileSuccess', (file) => getSVRChunkAsset(valueId, { chunk_identifier: file.uniqueIdentifier })
      .then(resolve).catch(reject));

    if (R.support) {
      R.on('fileAdded', (file) => {
        file.resumableObj.upload();
      });
    }
  });
};

export const getSVRAssetName = (svr, countryCode = 'US') => {
  const date = formatDateForUserShort(svr.created_at || svr.updated_at, countryCode);
  // Replace slashes with dashes for filename compatibility
  const safeDate = date.replace(/\//g, '-');
  return `site_visit_report_${safeDate}`;
};

export const getCorrectiveActionsCsvName = ({ farmId, templateId, svrId } = {}) => {
  const parts = ['EveryPig_Corrective_Actions'];
  if (farmId) parts.push(`Farm-${farmId}`);
  if (templateId) parts.push(`Template-${templateId}`);
  if (svrId) parts.push(`Svr-${svrId}`);
  parts.push(moment().format('MM-DD-YYYY-h-mm-a'));
  return `${parts.join('_')}.csv`;
};

export const getReportCsvName = ({ farmId, templateId } = {}) => {
  const parts = ['EveryPig_Reports'];
  if (templateId) parts.push(`Template-${templateId}`);
  if (farmId) parts.push(`Farm-${farmId}`);
  parts.push(moment().format('MM-DD-YYYY-h-mm-a'));
  return `${parts.join('_')}.csv`;
};

export function getUserAccessToFarmSVR(user, farmId, farmManagerId) {
  const { admin, caretaker, manager, owner, vet, super_admin, tenant_owner } = user.roles_map;
  const isUserHasAdminRole = admin || super_admin || tenant_owner;

  if (isUserHasAdminRole) {
    return { canEdit: true, canView: true };
  }
  if (!farmId && !farmManagerId) {
    return { canEdit: false, canView: false };
  }
  let isUserHasEditRoleOnFarm = false;
  let isUserHasViewRoleOnFarm = false;
  let isUserHasEditRoleOnCompany = false;
  let isUserHasViewRoleOnCompany = false;

  if (farmId) {
    isUserHasEditRoleOnFarm = manager.farm_ids?.includes(+farmId) || owner.farm_ids?.includes(+farmId)
      || vet.farm_ids?.includes(+farmId);
    isUserHasViewRoleOnFarm = caretaker.farm_ids?.includes(+farmId);
  }
  if (farmManagerId) {
    isUserHasEditRoleOnCompany = manager.company_ids?.includes(+farmManagerId)
      || owner.company_ids?.includes(+farmManagerId) || vet.company_ids?.includes(+farmManagerId);
    isUserHasViewRoleOnCompany = caretaker.company_ids?.includes(+farmManagerId);
  }
  const canEdit = isUserHasEditRoleOnFarm || isUserHasEditRoleOnCompany;
  const canView = canEdit || isUserHasViewRoleOnFarm || isUserHasViewRoleOnCompany;

  return {
    canEdit,
    canView,
  };
}

export function getSVRBackLink(farmId, templateId, query) {
  switch (query?.from) {
    case 'farmfeed':
      return '/farmfeed';
    case 'barnsheets':
      return `/barnsheets/farms/${farmId}/reports`;
    default:
      return `/report-farms/${farmId}/report-templates/${templateId}/reports`;
  }
}

// Questions a category-level "all good" confirmation can auto-pass:
// open-answer questions hold free text, non-common types hold notes/media,
// carried-forward failed ones need a corrected / not corrected decision
export function isSVRQuestionBulkPassable(question) {
  if (question?.open_answer) return false;
  if (question?.question_type && question.question_type !== 'common') return false;
  const answer = question?.answer;
  return !(answer?.carried_forward && answer?.value === 'fail');
}

export function isSVRCategoryAllPassed(category) {
  const passableQuestions = category.svr_questions.filter(isSVRQuestionBulkPassable);
  return passableQuestions.length > 0 && passableQuestions.every(({ answer }) => answer?.value === 'pass');
}

export function hasSVRCategoryAnswers(category) {
  return category.svr_questions.some(({ answer }) => answer?.value);
}

export function hasSVRCategoryFailedAnswers(category) {
  return category.svr_questions.some(({ answer }) => answer?.value === 'fail');
}

// Carried-forward questions still failed also require a corrected / not corrected decision
export function isSVRQuestionComplete(question) {
  const answer = question?.answer;
  if (!answer?.value) return false;
  if (answer.carried_forward && answer.value === 'fail') {
    return !!(answer.is_corrected || answer.not_corrected);
  }
  return true;
}

// Draft edit: categories answered individually or left with incomplete questions start revealed
export function shouldRevealSVRCategoryQuestions(category) {
  if (!hasSVRCategoryAnswers(category)) return false;
  return !isSVRCategoryAllPassed(category) ||
    category.svr_questions.some((question) => !isSVRQuestionComplete(question));
}

export function isSVRCategoriesEmpty(categories) {
  return !categories.some((category) => {
    return category?.svr_questions?.some(isSVRQuestionComplete);
  });
}

export function isAdditionalCategoryEmpty(additionalCategory) {
  return !additionalCategory?.svr_questions?.some(({ answer, question_type }) => {
    if (question_type === 'media') {
      return answer?.value?.length;
    }

    return answer?.value;
  });
}

export function checkSVRConfirmable(categories) {
  return categories.every((category) => {
    return category?.svr_questions?.every(isSVRQuestionComplete);
  });
}

export function getAnswersCountByAnswerType(svr, type) {
  let count = 0;

  svr.svr_categories.forEach(({ svr_questions }) => {
    svr_questions.forEach(({ answer, open_answer }) => {
      if (!open_answer && answer?.value === type && answer?.active && !answer?.is_corrected) count++;
    });
  });

  return count;
}

export function getAnswersCountCorrected(svr) {
  let count = 0;

  svr.svr_categories.forEach(({ svr_questions }) => {
    svr_questions.forEach(({ answer, open_answer }) => {
      if (!open_answer && answer?.is_corrected && answer?.active) count++;
    });
  });

  return count;
}

export function getAnswersCountByOpenAnswer(svr) {
  let count = 0;

  svr.svr_categories.forEach(({ svr_questions }) => {
    svr_questions.forEach(({ answer, open_answer }) => {
      if (open_answer && answer?.active && answer.value) {
        count++;
      }
    });
  });
  return count;
}

const isAnswerActive = ({ answer }) => answer?.active;
const sortQuestions = (questionOne, questionTwo) => {
  return questionOne?.answer?.position > questionTwo?.answer?.position ? 1 : -1;
};

function isQuestionsPresent({ svr_questions }) {
  return svr_questions.length;
}

const parseQuestions = (question) => ({
  ...question,
  label: question.item_key,
});

export const parseReportCategories = (categories = []) => categories
  .map((category) => ({
    ...category,
    name: category.item_key,
    svr_questions: category.svr_questions.filter(isAnswerActive).sort(sortQuestions).map(parseQuestions),
  }))
  .map(parseQuestions)
  .filter(isQuestionsPresent);

export const parseReportResponse = (data) => ({
  id: data.id,
  user: data.user,
  isDraft: data.is_draft,
  createdAt: data.created_at,
  categories: parseReportCategories(data.svr_categories),
  svr_template_id: data.svr_template_id,
  general_notes: data.general_notes,
  general_notes_section: data.general_notes_section,
  // Missing on reports cached offline before the setting existed - treat as enabled
  category_pass_all: data.category_pass_all !== false,
  action_plan: data.action_plan,
  action_plan_section: data.action_plan_section,
  photo_video_uploads: data.photo_video_uploads,
  svr_assets: data.svr_assets,
  correction_logs: data.correction_logs || [],
});

export const getReportReviewPath = (report) => {
  const { farm_id, id, svr_template_id } = report;
  return `/report-farms/${farm_id}/report-templates/${svr_template_id}/reports/${id}/review`;
};

export const getReportEditPath = (report) => {
  const { farm_id, id, svr_template_id } = report;
  return `/report-farms/${farm_id}/report-templates/${svr_template_id}/reports/${id}`;
};
